/* eslint-disable eqeqeq */
import Axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import { tagToTagLayerViewFormat } from "../Tags/helpers/Helpers";
import { extractApiErrorMessage } from "../common/helpers/ErrorUtils";

const API_BASE = import.meta.env.VITE_API_BASE;

const defaultAxiosOpts = {
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  withCredentials: true,
  maxRedirects: 0,
};

const axiosInstance = Axios.create(defaultAxiosOpts);

// Auth refresh function for use with axios-auth-refresh library
// In effect, this will be called after a 401 is received.
const refreshAuthTokens = async (rsp) => {
  const response = rsp.response;
  const tcUrl = window.location.href;
  const cutoff = response.headers.location.substring(
    0,
    response.headers.location.indexOf("?ReturnUrl=") + 11
  );
  const redirect_url = cutoff + encodeURIComponent(tcUrl);
  return axiosInstance
    .get(redirect_url, {
      transformResponse: [
        (data) => {
          const authResponse = JSON.parse(data);
          if (authResponse.status == 1) {
            //Auth successful, nop
          } else if (authResponse.status == 2) {
            //Credentials are missing, redirect to launchpad
            window.location.href = authResponse.location;
          } else if (authResponse.status == 3) {
            //Missing EULA (Use Dashboard page to accept for now)
            window.location.href = `${API_BASE}/preAuth.html?type=eula&ReturnUrl=${encodeURIComponent(tcUrl)}`;
          } else if (authResponse.status == 4) {
            //Missing Tenant (Use Dashboard page to accept for now)
            window.location.href = `${API_BASE}/preAuth.html?type=tenant&ReturnUrl=${encodeURIComponent(tcUrl)}`;
          } else {
            window.location.href = "/unauthorized";
          }
        },
      ],
    })
    .then(() => {
      console.log("Auth tokens refreshed");
    })
    .catch((err) => {
      console.error(`error while calling ${redirect_url} to get tokens`, err);
    });
};

// Attach auth refresh function to Axios interceptor. This library will retry requests once, and queue any requests
// that failed on a 401 for retry after auth tokens are received.
createAuthRefreshInterceptor(axiosInstance, refreshAuthTokens);

const createApiError = (err) => {
  const apiError = new Error(extractApiErrorMessage(err));

  if (err && typeof err === "object") {
    apiError.name = err.name || apiError.name;
    apiError.code = err.code;
    apiError.config = err.config;
    apiError.request = err.request;
    apiError.response = err.response;
    apiError.status = err.status ?? err.response?.status;
    apiError.statusText = err.statusText ?? err.response?.statusText;
    apiError.cause = err;
  }

  if (err?.stack) {
    apiError.stack = err.stack;
  }

  return apiError;
};

export const getTagsByAssetIds = async (assetIdList, axiosConfig) => {
  try {
    const body = { tagQuery: [], assetNumbers: [], assetIds: [...assetIdList] };
    let response = await axiosInstance.post(
      "/api/tags/find",
      body,
      axiosConfig
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getTagsByAssetNumbers = async (assetNumbers) => {
  try {
    const body = {
      tagQuery: [],
      assetNumbers: [...assetNumbers],
      assetIds: [],
    };
    let response = await axiosInstance.post("/api/tags/find", body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getTagsWithFilter = async (filterObject) => {
  try {
    const body = {
      tagQuery: [...filterObject],
    };
    let response = await axiosInstance.post("/api/tags/find", body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getBulkTagSummaries = async () => {
  try {
    let response = await axiosInstance.get("/api/tags/udbulktags");
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveBulkTagSummaries = async (summaries) => {
  try {
    let body = {
      UDsAndTagsToSave: summaries,
    };
    let response = await axiosInstance.post("/api/tags/udbulktags", body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveTag = async (tag) => {
  try {
    const body = window.Enbase.Common.Tag.Normalization.BuildAssetTagDTO(tag);
    const response = await axiosInstance.post("/api/tags/save", body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveTags = async (tagList) => {
  try {
    let tagSavePromises = [];
    for (const tag of tagList) {
      let promise = saveTag(tag);
      tagSavePromises.push(promise);
    }
    const proms = await Promise.allSettled(tagSavePromises);
    return proms;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveTagList = async (tagList) => {
  try {
    const body = {
      ...tagList,
    };
    const response = await axiosInstance.post("/api/taglists/save", body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveTagListAndTags = async (tagList, tags) => {
  let tagLayerIds = [];
  try {
    const tagLayersSaved = await saveTagLayers(tags);

    tagLayerIds = tagLayersSaved
      .filter((tagLayerResponse) => tagLayerResponse.status === "fulfilled")
      .map((tagLayerResponse) => tagLayerResponse.value.data.TCTagLayerId);

    const failedTagLayerSave = tagLayersSaved.find(
      (tagLayerResponse) => tagLayerResponse.status !== "fulfilled"
    );
    if (failedTagLayerSave) {
      throw failedTagLayerSave.reason instanceof Error
        ? failedTagLayerSave.reason
        : new Error(failedTagLayerSave.reason);
    }

    const body = {
      ...tagList,
      TagLayers: tagLayerIds,
    };
    const response = await axiosInstance.post("/api/taglists/save", body);
    return response;
  } catch (err) {
    if (tagLayerIds.length > 0) {
      await Promise.allSettled(
        tagLayerIds.map((tagLayerId) => deleteTagLayer(tagLayerId))
      );
    }

    throw createApiError(err);
  }
};

export const updateTagListMetadata = async (tagListId, updates) => {
  try {
    const body = {
      TCTagListId: tagListId,
      ...updates,
    };
    const response = await axiosInstance.post("/api/taglists/save", body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const duplicateTagListWithTags = async (sourceTagListId, newName) => {
  let duplicatedTagLayerIds = [];
  try {
    const sourceTagListResponse = await getTagListById(sourceTagListId);
    const sourceTagList = sourceTagListResponse.data;
    const sourceTags = sourceTagList.Tags || [];

    const tagsToSave = sourceTags.map((tag) => {
      const copiedTag = { ...tag };
      delete copiedTag.TagId;
      delete copiedTag.TCTagLayerId;
      delete copiedTag.AssetTagId;
      return copiedTag;
    });

    const savedTagLayers = await saveTagLayers(tagsToSave);

    duplicatedTagLayerIds = savedTagLayers
      .filter((tagLayerResponse) => tagLayerResponse.status === "fulfilled")
      .map((tagLayerResponse) => tagLayerResponse.value.data.TCTagLayerId);

    const failedTagLayerSave = savedTagLayers.find(
      (tagLayerResponse) => tagLayerResponse.status !== "fulfilled"
    );
    if (failedTagLayerSave) {
      throw failedTagLayerSave.reason instanceof Error
        ? failedTagLayerSave.reason
        : new Error(failedTagLayerSave.reason);
    }

    const duplicatedTagListPayload = {
      Name: newName,
      Description: sourceTagList.Description || "",
      HardwareCategory: sourceTagList.HardwareCategory || "",
      TagLayers: duplicatedTagLayerIds,
    };

    if (sourceTagList.TenantId) {
      duplicatedTagListPayload.TenantId = sourceTagList.TenantId;
    }
    if (sourceTagList.IsTagStandard) {
      duplicatedTagListPayload.IsTagStandard = sourceTagList.IsTagStandard;
    }

    const response = await axiosInstance.post(
      "/api/taglists/save",
      duplicatedTagListPayload
    );
    return response;
  } catch (err) {
    if (duplicatedTagLayerIds.length > 0) {
      await Promise.allSettled(
        duplicatedTagLayerIds.map((tagLayerId) => deleteTagLayer(tagLayerId))
      );
    }

    throw createApiError(err);
  }
};

export const getTagListById = async (TCTagListId) => {
  try {
    let response = await axiosInstance.get(`/api/taglists/${TCTagListId}`);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getAllTagListNames = async () => {
  try {
    let response = await axiosInstance.get(`/api/taglists/list`);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getTagListHistory = async (tagListId) => {
  try {
    let response = await axiosInstance.get(
      `/api/taglists/${tagListId}/history`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveTagLayer = async (tagLayerObj) => {
  try {
    let body = tagToTagLayerViewFormat(tagLayerObj);
    const response = await axiosInstance.post("/api/taglayers/save", body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveTagLayers = async (tagLayerList) => {
  try {
    let tagSavePromises = [];
    for (const tagLayer of tagLayerList) {
      let promise = saveTagLayer(tagLayer);
      tagSavePromises.push(promise);
    }
    const proms = await Promise.allSettled(tagSavePromises);
    return proms;
  } catch (err) {
    throw createApiError(err);
  }
};

export const deployTagToAsset = async (
  tag,
  assetId,
  assetNumber,
  overwriteTags
) => {
  try {
    let body = window.Enbase.Common.Tag.Normalization.BuildAssetTagDTO(tag);
    body.AssetTagId = 0;
    body.AssetId = assetId;
    body.AssetNumber = assetNumber;
    if (body.DisplayFormat) {
      // Value for DisplayFormat will get set by the backend
      delete body.DisplayFormat;
    }
    const requestUrl = overwriteTags
      ? "/api/tags/save?overwriteTags=True"
      : "/api/tags/save";
    const response = await axiosInstance.post(requestUrl, body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const deployTagsToAssets = async (
  deployList,
  tags,
  setDeployList,
  overwriteTags
) => {
  try {
    const deployEntries = deployList.map((deploy, originalIndex) => {
      return {
        deploy,
        originalIndex,
        tag: tags[deploy.TagIndex],
      };
    });
    const deployGroups = new Map();
    const normalizeIdentifier = (value) => {
      if (value === undefined || value === null || value === "") {
        return null;
      }
      const parsedValue = parseInt(value);
      return Number.isNaN(parsedValue) ? value : parsedValue;
    };
    for (const entry of deployEntries) {
      if (!deployGroups.has(entry.deploy.AssetId)) {
        deployGroups.set(entry.deploy.AssetId, []);
      }
      deployGroups.get(entry.deploy.AssetId).push(entry);
    }
    const phaseOneDeploys = [];
    const phaseTwoDeploys = [];

    for (const assetDeploys of deployGroups.values()) {
      for (const entry of assetDeploys) {
        const parentTagIdentifier = normalizeIdentifier(
          entry.tag?.ParentTagIdentifier
        );
        const dependsOnTagInSameBatch =
          parentTagIdentifier !== null &&
          assetDeploys.some((otherEntry) => {
            return (
              otherEntry.originalIndex !== entry.originalIndex &&
              normalizeIdentifier(otherEntry.tag?.TagIdentifier) ===
                parentTagIdentifier
            );
          });

        if (dependsOnTagInSameBatch) {
          phaseTwoDeploys.push(entry);
        } else {
          phaseOneDeploys.push(entry);
        }
      }
    }

    const proms = new Array(deployList.length);
    const runDeployPhase = async (phaseDeploys) => {
      const tagSavePromises = phaseDeploys.map(({ deploy, tag }) => {
        return deployTagToAsset(
          tag,
          deploy.AssetId,
          deploy.AssetNumber,
          overwriteTags
        );
      });
      return Promise.allSettled(tagSavePromises);
    };

    const phaseOneResults = await runDeployPhase(phaseOneDeploys);
    phaseOneResults.forEach((result, index) => {
      proms[phaseOneDeploys[index].originalIndex] = result;
    });

    // Only attempt a phase-two (dependent) deploy if its specific parent actually succeeded
    const succeededTagIdentifiersByAsset = new Map();
    phaseOneDeploys.forEach((entry, index) => {
      if (phaseOneResults[index].status !== "fulfilled") {
        return;
      }
      const assetId = entry.deploy.AssetId;
      if (!succeededTagIdentifiersByAsset.has(assetId)) {
        succeededTagIdentifiersByAsset.set(assetId, new Set());
      }
      succeededTagIdentifiersByAsset
        .get(assetId)
        .add(normalizeIdentifier(entry.tag?.TagIdentifier));
    });

    const phaseTwoDeploysToAttempt = [];
    phaseTwoDeploys.forEach((entry) => {
      const parentTagIdentifier = normalizeIdentifier(
        entry.tag?.ParentTagIdentifier
      );
      const succeededIdentifiers = succeededTagIdentifiersByAsset.get(
        entry.deploy.AssetId
      );
      const parentSucceeded =
        succeededIdentifiers?.has(parentTagIdentifier) ?? false;

      if (parentSucceeded) {
        phaseTwoDeploysToAttempt.push(entry);
      } else {
        proms[entry.originalIndex] = {
          status: "rejected",
          reason: new Error(
            `Skipped deploying '${entry.tag?.TagName}' because its parent tag (Tag Identifier ${entry.tag?.ParentTagIdentifier}) failed to deploy to this asset.`
          ),
        };
      }
    });

    // Known limitation: this only handles two dependency tiers within a single batch.
    const phaseTwoResults = await runDeployPhase(phaseTwoDeploysToAttempt);
    phaseTwoResults.forEach((result, index) => {
      proms[phaseTwoDeploysToAttempt[index].originalIndex] = result;
    });

    let newDeployList = [...deployList];
    proms.forEach((prom, index) => {
      if (prom.status === "rejected") {
        newDeployList[index].DeployStatus = "Failed";
        newDeployList[index].ErrorMessage = extractApiErrorMessage(prom.reason);
      } else {
        newDeployList[index].DeployStatus = "Complete";
      }
    });
    setDeployList(newDeployList);
    return proms;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getAssetsWithFilter = async (filterObject) => {
  try {
    const body = {
      tagQuery: [...filterObject],
      assetNumbers: [],
      assetIds: [],
    };
    let response = await axiosInstance.post("/api/assets/find", body);
    // Need assetId and assetNumber
    const idsOnly = response.data.map((asset) => {
      return {
        AssetId: asset.AssetId,
        AssetNumber: asset.AssetNumber,
      };
    });
    return idsOnly;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getLookupValues = async () => {
  try {
    let response = await axiosInstance.get(
      "/api/lookups/list?" +
        "name=TagType" +
        "&name=TagSourceType" +
        "&name=TagAlarmType" +
        "&name=AlarmSeverity" +
        "&name=GaugeType" +
        "&name=TagDataType" +
        "&name=NotificationPriority" +
        "&name=AlarmArmingType" +
        "&name=AlarmLimitRange" +
        "&name=AlarmState" +
        "&name=IsMech" +
        "&name=AlarmType"
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getAllCodeLegends = async () => {
  try {
    let response = await axiosInstance.get(`/api/codeLegends/list`);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getCodeLegendById = async (id) => {
  try {
    let response = await axiosInstance.get(`/api/codeLegends/${id}`);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getCodeLegendEntriesById = async (id) => {
  try {
    const response = await getCodeLegendById(id);
    return response.data.Entries;
  } catch (err) {
    throw createApiError(err);
  }
};

export const deleteTagListAndTags = async (id) => {
  try {
    const response = await axiosInstance.delete(
      `/api/taglists/${id}?deleteTagLayers=true`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getAvailableTenants = async () => {
  try {
    let response = await axiosInstance.get(
      `/DetechtionAccount/GetAvailableTenants`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const changeSessionTenant = async (id) => {
  try {
    let response = await axiosInstance.get(
      `/DetechtionAccount/ChangeSessionTenant/?desiredTenantId=${id}`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getAvailablePermissions = async () => {
  try {
    let response = await axiosInstance.get("/Account/GetAvailablePermissions");
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const queueTagSync = async (assetIds) => {
  try {
    const body = {
      assetIds: [...assetIds],
      monitoringDeviceIds: [],
    };
    let response = await axiosInstance.post("/api/assets/submitTagSync", body);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const appendTagLayerToTagList = async (tagListId, tagLayer) => {
  try {
    const body = tagToTagLayerViewFormat(tagLayer);
    const response = await axiosInstance.post(
      `/api/taglists/${tagListId}/append`,
      body
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const removeTagLayerFromTagList = async (tagListId, tagLayerId) => {
  try {
    const response = await axiosInstance.post(
      `/api/taglists/${tagListId}/remove/${tagLayerId}`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const deleteTagLayer = async (tagLayerId) => {
  try {
    const response = await axiosInstance.delete(`/api/taglayers/${tagLayerId}`);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

// Code Legend API calls

export const getMasterCodeLegendEntries = async () => {
  try {
    const response = await axiosInstance.get(
      "/api/codeLegends/standardCodeDescriptions/list"
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveCodeLegend = async (codeLegend) => {
  try {
    const response = await axiosInstance.post(
      "/api/codeLegends/save",
      codeLegend
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const deleteCodeLegend = async (codeLegendId) => {
  try {
    const response = await axiosInstance.delete(
      `/api/codeLegends/${codeLegendId}`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const deleteMasterCodeLegendEntry = async (entryId) => {
  try {
    const response = await axiosInstance.delete(
      `/api/codeLegends/standardCodeDescriptions/${entryId}`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveMasterCodeLegendEntry = async (entry) => {
  try {
    const response = await axiosInstance.post(
      "/api/codeLegends/standardCodeDescriptions/save",
      entry
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getAllUniversalDescriptors = async () => {
  try {
    const response = await axiosInstance.get(`/api/universalDescriptors/list`);
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const saveUniversalDescriptor = async (universalDescriptor) => {
  try {
    const response = await axiosInstance.post(
      "/api/universalDescriptors/save",
      universalDescriptor
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const disableUniversalDescriptor = async (universalDescriptorId) => {
  try {
    const response = await axiosInstance.post(
      `/api/universalDescriptors/disable/${universalDescriptorId}`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getUniversalDescriptorById = async (universalDescriptorId) => {
  try {
    const response = await axiosInstance.get(
      `/api/universalDescriptors/${universalDescriptorId}`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getUniversalDescriptor = async (UD) => {
  try {
    const response = await axiosInstance.get(
      `/api/universalDescriptors/ud/${encodeURIComponent(UD)}`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const enableUniversalDescriptor = async (universalDescriptorId) => {
  try {
    const response = await axiosInstance.post(
      `/api/universalDescriptors/enable/${universalDescriptorId}`
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};

export const getAssetTagsFromHub = async (assetNumber, assetId) => {
  try {
    const response = await axiosInstance.post(
      `/api/hubcommands/get-asset-tags-from-hub`,
      { assetId, assetNumber }
    );
    return response;
  } catch (err) {
    throw createApiError(err);
  }
};
